import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Scale, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

const num = (v) => parseFloat(v) || 0;
const fmt = (n) => Math.round(n).toLocaleString();

const Row = ({ label, value, onChange, readOnly, testid }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-slate-600">{label}</span>
    {readOnly ? (
      <span className="font-mono-clinical font-semibold text-slate-800 w-24 text-right" data-testid={testid}>{fmt(value)} mL</span>
    ) : (
      <div className="flex items-center gap-1">
        <Input type="number" min="0" step="1" inputMode="decimal" placeholder="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-20 h-8 font-mono-clinical text-right" />
        <span className="text-xs text-slate-400 w-6">mL</span>
      </div>
    )}
  </div>
);

// Interactive ins-vs-outs fluid-balance tally. IV-fluids "in" is fed live from the
// pump's infused volume so the chart ties to the running infusion.
export function FluidBalanceChart({ infused = 0, species = "dog", weight = 0 }) {
  const [ins, setIns] = useState({ bolus: "", oral: "" });
  const [outs, setOuts] = useState({ urine: "", vomit: "", other: "" });

  const inTotal = infused + num(ins.bolus) + num(ins.oral);
  const outTotal = num(outs.urine) + num(outs.vomit) + num(outs.other);
  const net = inTotal - outTotal;
  const scale = Math.max(inTotal, outTotal, 1);
  const w = (v) => `${Math.min(100, (v / scale) * 100)}%`;

  // Expected urine output benchmark (1–2 mL/kg/hr) for the per-kg context line.
  const kg = num(weight);
  const status =
    Math.abs(net) <= scale * 0.1
      ? { label: "Balanced", cls: "text-emerald-600", bar: "#10b981" }
      : net > 0
      ? { label: "Net positive — more in than out", cls: "text-sky-600", bar: "#0ea5e9" }
      : { label: "Net negative — more out than in", cls: "text-amber-600", bar: "#f59e0b" };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="fluid-balance-chart">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-1">
        <Scale className="w-3.5 h-3.5" /> Fluid balance · ins vs outs
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Tally everything going in against everything coming out to track this {species}&apos;s fluid balance.
        IV fluids are taken live from the pump.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* INS */}
        <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-3.5 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-sky-700 uppercase tracking-wider mb-1">
            <ArrowDownToLine className="w-3.5 h-3.5" /> Ins
          </div>
          <Row label="IV fluids (pump)" value={infused} readOnly testid="fb-in-iv" />
          <Row label="Flush / bolus" value={ins.bolus} onChange={(v) => setIns((s) => ({ ...s, bolus: v }))} testid="fb-in-bolus" />
          <Row label="Oral / enteral" value={ins.oral} onChange={(v) => setIns((s) => ({ ...s, oral: v }))} testid="fb-in-oral" />
          <div className="flex items-center justify-between border-t border-sky-200 pt-2 mt-1">
            <span className="text-xs font-bold text-sky-800">Total in</span>
            <span className="font-mono-clinical font-bold text-sky-800" data-testid="fb-in-total">{fmt(inTotal)} mL</span>
          </div>
        </div>

        {/* OUTS */}
        <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3.5 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">
            <ArrowUpFromLine className="w-3.5 h-3.5" /> Outs
          </div>
          <Row label="Urine" value={outs.urine} onChange={(v) => setOuts((s) => ({ ...s, urine: v }))} testid="fb-out-urine" />
          <Row label="Vomit / diarrhoea" value={outs.vomit} onChange={(v) => setOuts((s) => ({ ...s, vomit: v }))} testid="fb-out-vomit" />
          <Row label="Drain / other" value={outs.other} onChange={(v) => setOuts((s) => ({ ...s, other: v }))} testid="fb-out-other" />
          <div className="flex items-center justify-between border-t border-amber-200 pt-2 mt-1">
            <span className="text-xs font-bold text-amber-800">Total out</span>
            <span className="font-mono-clinical font-bold text-amber-800" data-testid="fb-out-total">{fmt(outTotal)} mL</span>
          </div>
        </div>
      </div>

      {/* paired bars */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-8 shrink-0">IN</span>
          <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-sky-400 transition-[width] duration-300" style={{ width: w(inTotal) }} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-8 shrink-0">OUT</span>
          <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-amber-400 transition-[width] duration-300" style={{ width: w(outTotal) }} />
          </div>
        </div>
      </div>

      {/* net balance */}
      <div className="mt-4 flex items-center justify-between rounded-lg border-2 px-4 py-3" style={{ borderColor: status.bar }} data-testid="fb-net-card">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Net balance</div>
          <div className={`text-sm font-semibold ${status.cls}`}>{status.label}</div>
        </div>
        <div className={`text-2xl font-bold font-mono-clinical ${status.cls}`} data-testid="fb-net">
          {net > 0 ? "+" : ""}{fmt(net)} mL
        </div>
      </div>
      {kg > 0 && (
        <p className="text-[11px] text-slate-400 mt-2">
          Reference urine output ≈ 1–2 mL/kg/hr ({fmt(kg * 1)}–{fmt(kg * 2)} mL/hr for {fmt(kg)} kg). Falling output may signal hypovolaemia or AKI — reassess.
        </p>
      )}
    </div>
  );
}
